import { createContext, useContext } from 'react';

import type { LocalVideoTrack } from 'livekit-client';

import type { RemoteVideoEntry } from '../hooks/useRoomAudio';
import type { UseRoomRecorderResult } from '../hooks/useRoomRecorder';

export interface RoomAudioContextValue {
  isMuted: boolean;
  forceMuted: boolean;
  toggleMute: () => void;
  speakingPeers: Set<string>;
  cleanup: () => void;
  // Optional: absent on older providers, and read as "not speaking".
  isLocalSpeaking?: boolean;
  getLocalStream: () => Promise<MediaStream>;
  getRemoteStreams: (exceptIdentity?: string) => MediaStream[];
  recorder: UseRoomRecorderResult;
  // Video-room extensions (absent on older providers — treat as audio-only).
  isCameraOn?: boolean;
  isScreenSharing?: boolean;
  toggleCamera?: () => void;
  toggleScreenShare?: () => void;
  computerAudioVolume?: number;
  setComputerAudioVolume?: (value: number) => void;
  // Ticket 96: gain on our OWN microphone, so everybody else hears us louder.
  // Optional like the rest of this block — a provider that predates it reads as
  // "no control available", and the button is simply not drawn.
  micGain?: number;
  setMicGain?: (value: number) => void;
  echoRisk?: boolean;
  echoMuted?: boolean;
  toggleEchoMute?: () => void;
  audioBlocked?: boolean;
  resumeAudio?: () => void;
  masterVolume?: number;
  setMasterVolume?: (value: number) => void;
  localCameraTrack?: LocalVideoTrack | null;
  localScreenTrack?: LocalVideoTrack | null;
  remoteVideoTracks?: Map<string, RemoteVideoEntry>;
  // Ticket 112: optional like the members around it — several tests and the
  // mini-player render without them.
  selfObsPreview?: boolean;
  setSelfObsPreview?: (on: boolean) => void;
  // Ticket 113: the room client log, for the stage's quality control.
  logClient?: (msg: string) => void;
}

export const RoomAudioContext = createContext<RoomAudioContextValue | null>(
  null,
);

export function useRoomAudioContext(): RoomAudioContextValue | null {
  return useContext(RoomAudioContext);
}
